<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import SidebarConfig from '~/components/module/SidebarConfig.vue';
import { useConfigParserStore } from '~/stores/configParser';
import { useConfigStore } from '~/stores/config';
import { useConfigProfilesStore } from '~/stores/config-profiles';


const { t, locale } = useI18n();
const toast = useToast();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Dialog'
  },
  sidebarWidth: {
    type: String,
    default: '15vw'
  },
  width: {
    type: String,
    default: '70vw'
  },
  height: {
    type: String,
    default: '95vh'
  },
  groupId: {
    type: Number,
    default: null
  },
  loading: {
    type: Boolean,
    default: false,
  }
});

const configParserStore = useConfigParserStore();
const configStore = useConfigStore();
const configProfilesStore = useConfigProfilesStore();

const titleId = computed(() => `dialog-title-${Math.random().toString(36).slice(2, 8)}`)
const configValues = computed(() => configParserStore.getConfigValues);
const allConfigElements = computed(() => configParserStore.getAllConfigElements);
const emit = defineEmits(['update:modelValue', 'submit', 'cancel', 'created']);
const showProfileNameDialog = ref(false);
const profileName = ref('');
const profileDescription = ref('');

const isOpen = ref(props.modelValue);

// Add this after your other imports
const existingProfileNames = computed(() =>
  configProfilesStore.profiles.map(p => p.profileName.toLowerCase().trim())
);

const isDuplicateProfileName = computed(() =>
  existingProfileNames.value.includes(profileName.value.toLowerCase().trim())
);

// For disabling the create button
const isCreateDisabled = computed(() =>
  !profileName.value.trim() || isDuplicateProfileName.value
);


const openProfileNameDialog = () => {
  // Get all enabled element keys from configValues
  const enabledKeys = Object.keys(configValues.value)
    .filter(key => key.startsWith('enable_') && configValues.value[key] === '1');

  if (enabledKeys.length === 0) {
    toast.add({
      severity: 'error',
      summary: t('configProfile.toast.createTitleError'),
      detail: t('configProfile.toast.emptyProfileError'),
      life: 3000
    });
    return;
  }
  showProfileNameDialog.value = true;
};

watch(() => props.modelValue, (newVal) => {
  isOpen.value = newVal;
});

watch(isOpen, async (newVal) => {
  emit('update:modelValue', newVal);
  if (newVal) {
    loading.value = true;
    await loadConfigFileData();
    loading.value = false;
  }
});

watch(locale, async () => {
  if (isOpen.value) {
    loading.value = true;
    await loadConfigFileData();
    loading.value = false;
  }
});

watch(() => props.groupId, async (newGroupId) => {
  if (newGroupId && isOpen.value) {
    loading.value = true;
    await configStore.getConfigFile(newGroupId);
    if (configStore.configStructureFileId) {
      await configStore.getConfigFileStructure(configStore.configStructureFileId, locale.value);
    }
    loading.value = false;
  }
});

const closeDialog = () => {
  isOpen.value = false;
};

const submitForm = async () => {
  const elementIds: number[] = [];
  const elementValue: { elementId: number; value: string }[] = [];

  // Get all enabled element keys from configValues
  const enabledKeys = Object.keys(configValues.value)
    .filter(key => key.startsWith('enable_') && configValues.value[key] === '1');

  console.log("Found enabled elements:", enabledKeys);

  // Process each enabled element
  for (const enableKey of enabledKeys) {
    // Extract the element ID from the key (remove 'enable_' prefix)
    const elementIdStr = enableKey.substring(7); // 'enable_'.length === 7
    const elementId = parseInt(elementIdStr, 10);

    if (!isNaN(elementId)) {
      // Add to elementIds array
      elementIds.push(elementId);

      // Find the corresponding element in allConfigElements
      let value = '';
      let elementPath = '';

      // Check if allConfigElements.value is a function and call it if needed
      const configElements = typeof allConfigElements.value === 'function'
        ? allConfigElements.value()
        : allConfigElements.value;

      // Find the element by ID
      if (Array.isArray(configElements)) {
        const element = configElements.find(el =>
          String(el.id) === elementIdStr || el.id === elementId
        );

        if (element && element.elementPath) {
          elementPath = element.elementPath;
          value = configValues.value[elementPath] || '';
        }
      }

      elementValue.push({
        elementId,
        value
      });

    }
  }

  try {
    const result = await configProfilesStore.createProfile({
      profileName: profileName.value,
      description: profileDescription.value,
      elementIds,
      elementValue
    });
    console.log('Submitting profile:', {
      profileName: profileName.value,
      description: profileDescription.value,
      elementIds,
      elementValue
    });


    console.log('Profile creation result:', result);
    toast.add({
      severity: 'success',
      summary: t('configProfile.toast.createTitleSuccess'),
      detail: t('configProfile.toast.createSuccessMessage', { profileName: profileName.value }),
      life: 3000
    });
    emit('created');
    closeDialog();
  } catch (error: any) {
    console.error('Profile creation error:', error);
    toast.add({
      severity: 'error',
      summary: t('configProfile.toast.createTitleError'),
      detail: error?.message || t('configProfile.toast.createErrorText'),
      life: 3000
    });
  }
  emit('submit');
  showProfileNameDialog.value = false;
  profileName.value = '';
  profileDescription.value = '';
};

const cancelForm = () => {
  emit('cancel');
  closeDialog();
  showProfileNameDialog.value = false;
};


const loadConfigFileData = async () => {
  if (!configStore.configStructureFileId) return false;

  try {
    const res = await configStore.getConfigFileStructure(
      configStore.configStructureFileId,
      locale.value
    );

    if (!res?.getConfigFileStructure) {
      toast.add({
        severity: 'error',
        summary: t('config.toast.loadError'),
        detail: t('config.toast.noConfigFileData'),
        life: 3000
      });
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error while loading the config file data', error);
    toast.add({
      severity: 'error',
      summary: t('config.toast.loadError'),
      detail: t('config.toast.noConfigFileData'),
      life: 3000
    });
    return false;
  }
};

// Module data for sidebar
const sortedModules = computed(() => configParserStore.getSortedModules);

const loading = ref(true);

// Mobile sidebar state
const isMobileSidebarOpen = ref(false);
const isMobile = ref(false);
const userHasToggledSidebar = ref(false);
const wasLastMobile = ref(false);

// Check if mobile
const checkMobile = () => {
  const currentIsMobile = window.innerWidth <= 768;
  const modeChanged = currentIsMobile !== wasLastMobile.value;

  isMobile.value = currentIsMobile;
  wasLastMobile.value = currentIsMobile;

  // Only auto-open on initial mobile load or when switching to mobile for the first time
  if (currentIsMobile && modeChanged && !userHasToggledSidebar.value) {
    isMobileSidebarOpen.value = true;
  } else if (!currentIsMobile) {
    // Reset when going back to desktop
    isMobileSidebarOpen.value = false;
    userHasToggledSidebar.value = false;
  }
};

const toggleMobileSidebar = () => {
  isMobileSidebarOpen.value = !isMobileSidebarOpen.value;
  userHasToggledSidebar.value = true;
};

const closeMobileSidebar = () => {
  isMobileSidebarOpen.value = false;
  userHasToggledSidebar.value = true;
};

onMounted(async () => {
  // Initialize mobile check
  checkMobile();
  window.addEventListener('resize', checkMobile);

  if (props.groupId) {
    try {
      // Load modules - replace with your actual loading method
      await loadConfigFileData();
      loading.value = true;
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: t('common.toast.error'),
        detail: t('config.toast.loadError'),
        life: 3000,
      });
    } finally {
      loading.value = false;
    }
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

</script>

<template>
  <div
    class="dialog-overlay"
    v-if="isOpen"
    role="dialog"
    :aria-labelledby="titleId"
    aria-modal="true"
    @click.self="closeDialog"
  >
    <div
      class="dialog-container"
      :style="{ width, height }"
      @keydown.esc.stop.prevent="closeDialog"
      tabindex="0"
    >
      <!-- Header spans entire dialog -->
      <div class="dialog-header">
        <button
          v-if="isMobile"
          class="mobile-sidebar-toggle-btn"
          @click="toggleMobileSidebar"
          :aria-label="t('common.actions.toggleMenu')"
        >
          <i class="pi pi-bars"></i>
        </button>
        <h3 :id="titleId" class="dialog-title">{{ title }}</h3>
        <button type="button" class="close-button" @click="closeDialog" aria-label="Close">×</button>
      </div>

      <!-- Main: sidebar + content -->
      <div class="dialog-main">
        <!-- Mobile backdrop -->
        <div
          v-if="isMobile && isMobileSidebarOpen"
          class="mobile-sidebar-backdrop"
          @click="closeMobileSidebar"
        ></div>

        <div
          class="dialog-sidebar"
          :style="{ width: sidebarWidth }"
          :class="{
            'sidebar-open': isMobileSidebarOpen,
            'sidebar-closed': isMobile && !isMobileSidebarOpen
          }"
        >
          <div class="sidebar-header" v-if="isMobile">
            <h4>{{ t('config.sidebar.modules') }}</h4>
            <button class="sidebar-close-btn" @click="closeMobileSidebar" :aria-label="t('common.actions.close')">
              <i class="pi pi-times"></i>
            </button>
          </div>
          <slot name="sidebar">
            <SidebarConfig
              :sorted-modules="sortedModules"
              :loading="loading"
              :show-title="false"
            />
          </slot>
        </div>

        <div class="dialog-content">
          <div class="dialog-body">
            <form @submit.prevent="submitForm">
              <slot />
            </form>
          </div>
        </div>
      </div>
      <!-- Footer spans entire dialog -->
      <div class="dialog-footer">
        <Button type="button" class="button submit" @click="openProfileNameDialog" :label="$t('configProfile.create')" />
        <Button type="button" class="button cancel" @click="cancelForm" :label="$t('configProfile.cancel')" />
      </div>
      <Dialog
        v-model:visible="showProfileNameDialog"
        :header="$t('configProfile.nameDialog')"
        :modal="true"
        :style="{ zIndex: 1000 }"
        class="profile-name-dialog">
        <form @submit.prevent="submitForm">
          <div class="p-field">
            <label for="profile-name">{{ $t('configProfile.nameLabel') }}</label>
            <InputText id="profile-name" v-model="profileName" class="w-full" :class="{'input-error': isDuplicateProfileName }" autofocus />
            <span v-if="isDuplicateProfileName" class="error-message">
             {{ $t('configProfile.toast.duplicateProfileNameError') }}
           </span>
          </div>
          <div class="p-field mt-3">
            <label for="profile-description">{{$t('configProfile.description')}}</label>
            <Textarea id="profile-description" v-model="profileDescription" class="w-full mt-2" rows="3" />
          </div>
        </form>
        <template #footer>
          <Button :label="$t('configProfile.create')" class="button submit" @click="submitForm"   :disabled="isCreateDisabled" />
          <Button :label="$t('configProfile.cancel')" class="button cancel" @click=" () => {showProfileNameDialog = false; profileName.value=''; profileDescription.value=''}" />
        </template>
      </Dialog>

    </div>
  </div>
</template>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  padding: 12px;
  box-sizing: border-box;
}

.dialog-container {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;

  background-color: hsl(var(--secondary-nav-bg));
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  outline: none;


  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 24px);
  min-height: 0;
}

.dialog-header {
  grid-row: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 16px 20px;
  background-color: hsl(var(--secondary-nav-bg));
  border-bottom: 1px solid hsl(var(--border));
  position: relative;
  min-height: 40px;
  z-index: 2;
}

.dialog-header h3 {
  margin: 0;
  font-weight: 500;
  flex: 1;
}

.dialog-title {

  padding-left: 48px;
}

.mobile-sidebar-toggle-btn {
  display: none;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
  border: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: absolute;
  top: 8px;
  left: 8px;
}

.mobile-sidebar-toggle-btn:hover {
  background: hsl(var(--primary) / 0.9);
  transform: scale(1.05);
}

.mobile-sidebar-toggle-btn:active {
  transform: scale(0.95);
}

.close-button {
  margin-left: auto;
  width: 36px;
  height: 36px;
  display: inline-grid;
  place-items: center;
  background: none;
  border: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #999;
  border-radius: 6px;
  flex-shrink: 0;
}

.close-button:hover {
  background: #f5f5f5;
  color: #666;
}

.close-button:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.dialog-main {
  grid-row: 2;
  display: flex;
  min-height: 0;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.dialog-sidebar {
  background-color: hsl(var(--secondary-nav-bg));
  border-right: 1px solid hsl(var(--border));
  flex-shrink: 0;

  min-height: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  padding-bottom: 24px;
  box-sizing: border-box;
  transition: transform 0.3s ease;
}

/*
  Important:
  SidebarConfig probably has its own height/overflow rules.
  We force it to stop using viewport height or fixed positioning.
*/
:deep(.dialog-sidebar .module-sidebar),
:deep(.dialog-sidebar aside),
:deep(.dialog-sidebar nav) {
  position: static !important;
  transform: none !important;

  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;

  overflow: visible !important;
}

:deep(.dialog-sidebar .module-sidebar) {
  width: 100% !important;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid hsl(var(--border));
  background-color: hsl(var(--secondary-nav-bg));
  flex-shrink: 0;
}

.sidebar-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.sidebar-close-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(var(--foreground));
  transition: background 0.2s ease;
}

.sidebar-close-btn:hover {
  background: hsl(var(--card-foreground) / 0.1);
}

.dialog-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  height: 100%;
}

.dialog-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 20px;
}

.dialog-footer {
  grid-row: 3;
  padding: 16px 20px;
  background-color: hsl(var(--secondary-nav-bg));
  border-top: 1px solid hsl(var(--border));
  text-align: right;
  z-index: 2;
}

.button {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  margin-left: 10px;
}

.cancel {
  background-color: #fbc02d;
}

.submit {
  background-color: #689f38;
  color: #fff;
}

.input-error {
  border: 1px solid #e53935 !important;
  background: #fff6f6;
}

.error-message {
  color: #e53935;
  font-size: 0.9em;
  margin-top: 4px;
  display: block;
}

.mobile-sidebar-backdrop {
  display: none;
}

:deep(.profile-name-dialog) {
  z-index: 1000 !important;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .mobile-sidebar-toggle-btn {
    display: flex !important; /* Show on mobile */
  }

  .dialog-main {
    flex-direction: row; /* Keep as row to maintain layout */
  }

  .dialog-sidebar {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;

    width: 280px !important;
    max-width: 280px;
    height: auto;
    z-index: 1001;

    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    transform: translateX(-100%);

    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 24px;
  }

  .dialog-sidebar.sidebar-open {
    transform: translateX(0);
  }

  .dialog-sidebar.sidebar-closed {
    transform: translateX(-100%);
  }

  .mobile-sidebar-backdrop {
    display: block;
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }

  .dialog-content {
    width: 100% !important;
    flex: 1 1 100% !important;
  }

  /* Hide nested sidebar toggle inside SidebarConfig on mobile */
  :deep(.mobile-sidebar-toggle) {
    display: none !important;
  }

  :deep(.dialog-sidebar .mobile-sidebar-backdrop) {
    display: none !important;
  }

  :deep(.dialog-sidebar .icon-btn-close) {
    display: none !important;
  }

  :deep(.dialog-body .grid),
  :deep(.dialog-body .p-grid),
  :deep(.dialog-body .p-field),
  :deep(.dialog-body .field) {
    display: flex !important;
    flex-direction: column !important;
  }

  :deep(.dialog-body [class*='col-']),
  :deep(.dialog-body .p-col),
  :deep(.dialog-body .p-col-12),
  :deep(.dialog-body .p-col-6),
  :deep(.dialog-body .p-col-4),
  :deep(.dialog-body .p-col-3) {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
    padding: 0.5rem 0 !important;
  }

  :deep(.dialog-body .p-inputtext),
  :deep(.dialog-body .p-dropdown),
  :deep(.dialog-body .p-calendar),
  :deep(.dialog-body .p-inputnumber),
  :deep(.dialog-body .p-inputtextarea),
  :deep(.dialog-body .p-multiselect),
  :deep(.dialog-body .p-chips) {
    width: 100% !important;
  }
}

/* Tablet adjustments */
@media (max-width: 1024px) and (min-width: 769px) {
  .dialog-sidebar {
    width: 200px !important;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .dialog-sidebar {
    width: 85vw !important;
    max-width: 85vw;
  }

  .dialog-header {
    padding: 12px 16px;
  }

  .dialog-header h3 {
    font-size: 1.1rem;
  }

  .dialog-footer {
    padding: 12px 16px;
  }

  .button {
    margin-left: 6px;
    padding: 8px 12px;
  }
}
</style>